<template>
  <div>
    <mobialis-form-with-validation
      @onValidate="continueForm"
      v-av-analytics-form-submit="{ elementText: $t('message.common.continue') }"
    >
      <div class="mb-4" v-if="!isAVRMobile">
        <h5>{{ getBusinessName }}</h5>
      </div>
      <div class="row justify-content-between">
        <div class="col" :class="{ 'col-sm-7': showBlocks }">
          <LetUsKnowHero v-if="isAVRDesktop || isAVREmbed"></LetUsKnowHero>
          <mobialis-text-input-with-validation
            :rules="{ required: true, regex: /^([^0-9]*)$/ }"
            :label="$t('message.common.firstName')"
            v-model="firstNameDisplay"
            vid="firstName"
            data-cy="firstName"
          ></mobialis-text-input-with-validation>
          <mobialis-text-input-with-validation
            :rules="{ required: true, regex: /^([^0-9]*)$/ }"
            :label="$t('message.common.lastName')"
            v-model="lastName"
            vid="lastName"
            data-cy="lastName"
          ></mobialis-text-input-with-validation>
          <mobialis-text-input-with-validation
            rules="required|length:10"
            :label="$t('message.common.phoneNumber')"
            v-model="phoneNumber"
            vid="phoneNumber"
            placeholder="(   )"
            mask-type="phoneNumber"
            type="tel"
            data-cy="phoneNumber"
            :error-message="avErrors['phoneNumber']"
          ></mobialis-text-input-with-validation>
          <!-- Render postal code/zip code field if the toggle is on in SDA -->
          <template v-if="getFeatureList.creditPostalCodeFirstPage">
            <mobialis-text-input-with-validation
              v-if="getThisIsAmerica"
              rules="required|length:5"
              :label="$t('message.common.zipCode')"
              v-model="zipCode"
              vid="zipCode"
              data-cy="zipCode"
              mask-type="zipCode"
            ></mobialis-text-input-with-validation>
            <mobialis-text-input-with-validation
              v-else
              rules="required|length:6"
              :label="$t('message.common.postalCode')"
              v-model="postalCode"
              vid="postalCode"
              mask-type="postalCode"
              data-cy="postalCode"
            ></mobialis-text-input-with-validation>
          </template>
          <mobialis-text-input-with-validation
            rules="required|email"
            :label="$t('message.common.emailAddress')"
            v-model="emailAddress"
            vid="emailAddress"
            data-cy="emailAddress"
            type="email"
            :error-message="avErrors['emailAddress']"
          ></mobialis-text-input-with-validation>
        </div>
        <div v-if="showBlocks" class="col-sm-4 d-none d-sm-block">
          <CreditInfoCards :showFallbackCards="true"></CreditInfoCards>
        </div>
        <div class="col-12">
          <mobialis-check-box-with-validation
            rules="required:true"
            :label="TermsText"
            vid="tandc"
            id="tandc"
            data-cy="tAndC"
          ></mobialis-check-box-with-validation>
        </div>
      </div>
      <mobialis-button data-cy="submit" type="submit" class="mt-4 mb-1">{{
        $t('message.common.continue')
      }}</mobialis-button>
      <p class="d-flex justify-content-center small my-0">{{ $t('message.credit.disclaimer.noAffectScore') }}</p>
    </mobialis-form-with-validation>
    <div v-if="displayEquifaxBranding && !isAVRMobile">
      <p class="mt-3 font-size-10 color-primary-grey">{{ $t('message.credit.disclaimer.equifax') }}</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { avErrorMixin } from '@/mixins';
import LetUsKnowHero from '@/components/credit/hero/LetUsKnow.vue';
import log from '@/services/log';
import CreditInfoCards from '@/components/credit/CreditInfoCards.vue';
import { CreditDataSourceCA } from '@/utils/enums';

export default {
  name: 'CreditForm1',
  data() {
    return {
      CreditDataSourceCA,
      firstName: '',
      lastName: '',
      emailAddress: '',
      phoneNumber: '',
      postalCode: '',
      zipCode: '',
    };
  },
  components: {
    CreditInfoCards,
    LetUsKnowHero,
  },
  mixins: [avErrorMixin],
  computed: {
    ...mapGetters('business', [
      'getBusinessName',
      'getThisIsAmerica',
      'getFeatureList',
      'isKijijiDealer',
      'prequalificationFinanceProviders',
      'getDefaultCreditDataSourceCA',
      'isPrequalificationEnabled',
      'isTDPrequalEnabled',
      'displayEquifaxBranding',
    ]),
    TermsLink() {
      return this.$t('message.credit.form1Link');
    },
    TermsText() {
      let text = '';
      if (
        this.isKijijiDealer &&
        this.isTDPrequalEnabled &&
        this.getDefaultCreditDataSourceCA === CreditDataSourceCA.EQUIFAX
      ) {
        text = this.$t('message.credit.kijijiEquifaxTDCreditLegal', { dealer: this.getBusinessName });
      } else if (
        this.isKijijiDealer &&
        this.getDefaultCreditDataSourceCA === CreditDataSourceCA.EQUIFAX_ID_AUTH_NEIGHBOURHOOD_CREDIT
      ) {
        text = this.$t('message.credit.kijijiEquifaxCreditService', { dealer: this.getBusinessName });
      } else if (
        this.isKijijiDealer &&
        this.isPrequalificationEnabled &&
        this.getDefaultCreditDataSourceCA === CreditDataSourceCA.EQUIFAX
      ) {
        text = this.$t('message.credit.kijijiEquifaxCreditLegal', { dealer: this.getBusinessName });
      } else if (
        !this.isKijijiDealer &&
        this.isTDPrequalEnabled &&
        this.getDefaultCreditDataSourceCA === CreditDataSourceCA.EQUIFAX
      ) {
        text = this.$t('message.credit.equifaxTDCreditLegal', { dealer: this.getBusinessName });
      } else if (!this.isKijijiDealer && this.getDefaultCreditDataSourceCA === CreditDataSourceCA.EQUIFAX) {
        text = this.$t('message.credit.equifaxCreditLegal', { dealer: this.getBusinessName });
      } else if (
        !this.isKijijiDealer &&
        this.getDefaultCreditDataSourceCA === CreditDataSourceCA.NEIGHBOURHOOD_CREDIT
      ) {
        text = this.$t('message.credit.neighbourhoodCreditLegal', { dealer: this.getBusinessName });
      } else if (!this.isKijijiDealer && this.getDefaultCreditDataSourceCA === CreditDataSourceCA.CREDIT_PLUS) {
        text = this.$t('message.eCommerceV2.creditTandC', {
          termsLink: this.$t('message.eCommerceV2.termsLink'),
          dealerName: this.getBusinessName,
          creditProvider: this.$t('message.common.dTransUnion'),
        });
      } else {
        text = `${this.$t('message.credit.form1checkBox', { link: this.TermsLink, dealer: this.getBusinessName })}`;
      }
      return text;
    },
    firstNameDisplay: {
      get() {
        return this.firstName;
      },
      set(value) {
        this.firstName = value;
      },
    },
  },
  mounted() {
    this.firstName = this.$store.state.user.firstName;
    this.lastName = this.$store.state.user.lastName;
    this.emailAddress = this.$store.state.user.emailAddress;
    this.phoneNumber = this.$store.state.user.phoneNumber;
    this.postalCode = this.$store.state.user.postalCode;
    this.zipCode = this.$store.state.user.zipCode;
    this.$store.commit('SET_LOADING', false);
  },
  methods: {
    async continueForm(isValid) {
      if (!isValid) {
        return;
      }
      try {
        this.$store.commit('user/SET_FIRST_NAME', this.firstName);
        this.$store.commit('user/SET_LAST_NAME', this.lastName);
        this.$store.commit('user/SET_EMAIL_ADDRESS', this.emailAddress);
        this.$store.commit('user/SET_PHONE_NUMBER', this.phoneNumber);
        if (this.getThisIsAmerica) {
          this.$store.commit('user/SET_ZIP_CODE', this.zipCode);
        } else {
          this.$store.commit('user/SET_POSTAL_CODE', this.postalCode);
        }
        this.$store.commit('SET_LOADING', true);
        await this.$store.dispatch('lead/createLead');

        try {
          await this.$store.dispatch('lead/updateLead');
          this.$router.push({ name: `creditform2-${this.baseRoute}` });
        } catch (err) {
          log('Error in Creditform 1 - update lead', err);
          this.$router.push({ name: `credit-in-error-${this.baseRoute}` });
        }
      } catch (err) {
        if (!this.handleServerSideError(err)) {
          log('Error in Creditform 1 - create lead', err);
          this.$router.push({ name: `credit-in-error-${this.baseRoute}` });
        }
      } finally {
        this.$store.commit('SET_LOADING', false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.form-check::v-deep {
  margin-top: 5px;
  label.form-check-label {
    p {
      margin-top: 0;
      margin-bottom: 7px;
    }
    ul,
    ol {
      padding-left: 25px;
    }
    p,
    ul,
    li,
    ol {
      color: #4b5863;
      font-size: 12px;
      line-height: 18px;
    }
  }
}
</style>
